import { Link, useNavigate } from '@tanstack/react-router';
import { useLayoutEffect, useRef } from 'react';

import { FixtureBadge } from '@/components/layout/FixtureBadge';
import { MarketSwitcher } from '@/components/layout/MarketSwitcher';
import { ThemeSwitcher } from '@/components/layout/ThemeSwitcher';
import { UserMenu } from '@/features/auth/UserMenu';
import { SymbolPicker } from '@/features/dashboard/SymbolPicker';
import { useAsOf } from '@/lib/asof/use-asof';
import { useMarket } from '@/lib/market/market-context';

/**
 * ARCHITECTURE §10.1：`__root.tsx` ＝ TopNav（含 as_of 時光機控制項）+ Sidebar + Outlet + Disclaimer。
 *
 * 這一條 bar 高 44px，因為它每天都在，而每多 8px 就是每天少看到 8px 的資料。
 */
export function TopNav() {
  const { market } = useMarket();
  const { rewound, asOf, param } = useAsOf();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);

  // 側邊欄以 `sticky top-[var(--topnav-h)]` 固定在這條 bar 下方。bar 的高度不是常數
  // （倒帶時多一條說明列、窄螢幕會縮），所以量實際高度寫進 CSS 變數，而不是寫死 px。
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (el === null) return;
    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty('--topnav-h', `${el.offsetHeight}px`);
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <header
      ref={headerRef}
      // 瀏海那一圈（--safe-top）由 header 自己的底色墊上，量到的 --topnav-h 也就把它算進去了。
      className="sticky top-0 z-30 bg-surface-1/90 pt-[var(--safe-top)] backdrop-blur"
    >
      {/* 倒帶時整個 app 頂上多一條 2px 的青藍橫條。它是全域狀態，所以它在全域可見的位置。 */}
      <div
        className={rewound ? 'h-0.5 bg-timewarp' : 'h-0.5 bg-transparent'}
        aria-hidden="true"
      />
      {/* 窄螢幕（< sm）折成兩列：第一列品牌／市場／主題／帳號，第二列是整條的 as_of 控制項
          （±1 日按鈕要 44px，一列塞不下）。右側群組在 < sm 是 `contents`，
          它的子元素直接參與這一列的 flex-wrap，AsOfControl 用 order 排到最後自成一列。 */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 border-b border-border-1/60 py-2 pr-[max(0.75rem,var(--safe-right))] pl-[max(0.75rem,var(--safe-left))] sm:flex-nowrap sm:gap-4 sm:py-2.5 sm:pr-[max(1.25rem,var(--safe-right))] sm:pl-[max(1.25rem,var(--safe-left))]">
        {/* 品牌標識：Stockable 六點幾何陣列標誌 */}
        <div className="flex min-w-0 items-center gap-2 max-sm:mr-auto sm:gap-3">
          <Link
            to="/$market/dashboard"
            params={{ market }}
            aria-label="Stockable 儀表板"
            className="group -m-2 flex shrink-0 items-center gap-2.5 p-2 transition-opacity hover:opacity-85 sm:m-0 sm:p-0"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-accent" fill="currentColor" aria-hidden="true">
              <circle cx="6" cy="6" r="2.5" />
              <circle cx="12" cy="6" r="2.5" />
              <circle cx="18" cy="6" r="2.5" />
              <circle cx="6" cy="14" r="2.5" />
              <circle cx="12" cy="14" r="2.5" />
              <circle cx="18" cy="14" r="2.5" />
            </svg>
            <span className="hidden text-16 font-bold tracking-tight text-fg-1 sm:inline">Stockable</span>
          </Link>

          <span className="hidden h-5 w-px bg-border-1 sm:inline-block" aria-hidden="true" />

          {/* 市場切換 */}
          <MarketSwitcher />
        </div>

        {/* 右側操作區 */}
        <div className="flex shrink-0 items-center gap-1.5 max-sm:contents sm:gap-2">
          <FixtureBadge />
          {/* 全站搜尋：代號、中文名、英文名、拼音／注音首字母；選到就進該檔個股頁。 */}
          <SymbolPicker
            market={market}
            symbol=""
            asOf={param}
            onSelect={(hit) => {
              void navigate({
                to: '/$market/dashboard',
                params: { market: hit.market },
                search: (prev: Record<string, unknown>) => ({ ...prev, symbol: hit.symbol }),
              });
            }}
            trigger={(open) => (
              <button
                type="button"
                onClick={open}
                aria-label="搜尋標的"
                className="flex h-10 min-w-10 shrink-0 items-center justify-center gap-2 glass rounded-full px-3 text-12 text-muted transition-all hover:border-border-2 hover:bg-surface-2 sm:h-auto sm:min-w-0 sm:py-1"
              >
                <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 text-muted" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="8.5" cy="8.5" r="5.5" />
                  <path d="M12.5 12.5L16.5 16.5" strokeLinecap="round" />
                </svg>
                <span className="hidden md:inline">搜尋標的…</span>
                <span className="ml-1 hidden rounded border border-border-1/60 bg-surface-1 px-1.5 py-0.5 font-mono text-10 font-semibold text-fg-2 shadow-2xs lg:inline">
                  ⌘K
                </span>
              </button>
            )}
          />
          <ThemeSwitcher />

          {/* 曜石黑主動作按鈕 (Obsidian Primary CTA)。lg 以下 AI 助理已是底部分頁列的一格。 */}
          <Link
            to="/$market/assistant"
            params={{ market }}
            aria-label="快速分析"
            className="hidden items-center gap-1 glass rounded-full px-2.5 sm:px-3.5 py-1.5 text-12 font-medium text-fg-1 transition-all shrink-0 lg:flex"
          >
            <span className="text-11 text-emerald-400 font-bold">+</span>
            <span className="hidden sm:inline">快速分析</span>
          </Link>
          {/* 帳號：載入中骨架／「登入」膠囊／頭像選單（features/auth/UserMenu.tsx） */}
          <UserMenu />
        </div>
      </div>

      {rewound && asOf !== undefined && (
        <div className="border-b border-timewarp/30 bg-timewarp-soft px-3 py-1 text-11 text-timewarp-ink">
          你正在看 <span className="mono font-semibold">{asOf}</span>{' '}
          當時「可被知道」的世界：只有那天以前發生、而且那天以前已經公布的資料。
          任何一個沒有跟著退回去的數字，都是一個還沒走過 PIT 層的漏洞。
        </div>
      )}
    </header>
  );
}
